import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { refreshHecSrcCookie } from "./hec-src-refresh.client";

/**
 * Refresh `hec_src` on every committed soft navigation (COM-787).
 *
 * Skips the initial commit: the hard document load that produced it already
 * ran the inline backstop (and, on a cache MISS, the server refresh). It
 * compares against the key the previous run saw, not the initial key: Back
 * to the hard-loaded entry restores its key (`"default"`), and that is still a
 * soft navigation. The
 * `prerendering` check is belt-and-braces only — a soft navigation normally
 * needs an activated page — mirroring the backstop's prerender gate. Pass
 * `enabled=false` in the Builder editor, where GTM and the backstop are
 * suppressed too.
 */
export function useHecSrcSoftNavRefresh(enabled: boolean): void {
  const { key } = useLocation();
  const seenKey = useRef(key);

  useEffect(() => {
    const isSoftNav = seenKey.current !== key;
    seenKey.current = key;
    if (!enabled || !isSoftNav) return;
    if ((document as Document & { prerendering?: boolean }).prerendering) {
      return;
    }
    refreshHecSrcCookie();
  }, [enabled, key]);
}
